import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    ArrowLeft,
    Calendar,
    Loader2,
    Receipt,
    AlertTriangle,
    RefreshCw,
    Filter,
    Edit2,
    Trash2,
    Lock,
    FileDown,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContext';
import { AmministrazioneService } from '../services/apiService';
import { MovimentoContabile, MovimentoEditRequest, RegistroMovimentiPreFilter } from '../types';

/*
 * Registro Movimenti: vista unica dei movimenti contabili dell'azienda.
 *
 * Il filtro sorgente arriva da query string `?pre=ALL|PRATICHE|SERVIZI`
 * (default ALL, registro generale). La paginazione è lato client: il filtro
 * periodo tiene i risultati contenuti.
 */

// Primo giorno di utilizzo del gestionale: base del "Da Riscuotere" cumulativo
// e default del preset "Dall'inizio".
export const REGISTRO_START_DATE = '2026-06-01';

type PeriodoPreset = 'INIZIO' | 'MESE' | 'MESI_3' | 'MESI_6' | 'ANNO' | 'CUSTOM';

function isoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

function todayIso(): string {
    return isoDate(new Date());
}

function isoMonthsAgo(months: number): string {
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    return isoDate(d);
}

function isoYearAgo(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return isoDate(d);
}

function rangeForPreset(preset: PeriodoPreset, customFrom: string, customTo: string):
    { from: string; to: string } {
    switch (preset) {
        case 'INIZIO':
            return { from: REGISTRO_START_DATE, to: todayIso() };
        case 'MESE':
            return { from: isoMonthsAgo(1), to: todayIso() };
        case 'MESI_3':
            return { from: isoMonthsAgo(3), to: todayIso() };
        case 'MESI_6':
            return { from: isoMonthsAgo(6), to: todayIso() };
        case 'ANNO':
            return { from: isoYearAgo(), to: todayIso() };
        case 'CUSTOM':
            return { from: customFrom || REGISTRO_START_DATE, to: customTo || todayIso() };
    }
}

function parsePreFilter(raw: string | null): RegistroMovimentiPreFilter {
    if (raw === 'PRATICHE' || raw === 'SERVIZI') return raw;
    return 'ALL';
}

function sottotitolo(pre: RegistroMovimentiPreFilter): string {
    if (pre === 'PRATICHE') return 'Tutti gli incassi, le spese e i costi collegati ai mandati.';
    if (pre === 'SERVIZI') return 'Incassi e rimborsi dei servizi fiduciari.';
    return 'Tutte le entrate e le uscite dell\'azienda — registro contabile generale.';
}

// Etichetta breve della categoria, usata in tabella e nel badge.
function labelCategoria(cat?: string): string {
    switch (cat) {
        case 'ACCONTO_MANDATO': return 'Acconto mandato';
        case 'MOVIMENTO_MANDATO_ENTRATA': return 'Incasso mandato';
        case 'MOVIMENTO_MANDATO_USCITA': return 'Uscita mandato';
        case 'MOVIMENTO_SERVIZIO_ENTRATA': return 'Incasso servizio';
        case 'MOVIMENTO_SERVIZIO_USCITA': return 'Rimborso servizio';
        case 'SPESA_COLLABORATORE_EXTRA': return 'Spesa collaboratore';
        case 'SPESA_COLLABORATORE_REPORT': return 'Spesa report';
        // Uscita pagata direttamente con la carta aziendale: non è un rimborso al
        // collaboratore e deve distinguersi a colpo d'occhio.
        case 'SPESA_CARTA_AZIENDALE': return 'Carta aziendale';
        case 'COMPENSO_COLLABORATORE': return 'Compenso';
        case 'MOVIMENTO_MANUALE_ENTRATA': return 'Entrata una tantum';
        case 'MOVIMENTO_MANUALE_USCITA': return 'Uscita una tantum';
        default: return '—';
    }
}

function colorePerCategoria(cat?: string): string {
    switch (cat) {
        case 'ACCONTO_MANDATO':
        case 'MOVIMENTO_MANDATO_ENTRATA':
        case 'MOVIMENTO_SERVIZIO_ENTRATA':
        case 'MOVIMENTO_MANUALE_ENTRATA':
            return 'bg-emerald-100 text-emerald-800';
        case 'MOVIMENTO_MANDATO_USCITA':
        case 'MOVIMENTO_SERVIZIO_USCITA':
        case 'SPESA_COLLABORATORE_EXTRA':
        case 'SPESA_COLLABORATORE_REPORT':
        case 'COMPENSO_COLLABORATORE':
        case 'MOVIMENTO_MANUALE_USCITA':
            return 'bg-rose-100 text-rose-800';
        // Viola come il badge "Carta az." delle spese in /approvazioni.
        case 'SPESA_CARTA_AZIENDALE':
            return 'bg-purple-100 text-purple-800';
        default:
            return 'bg-slate-100 text-slate-700';
    }
}

const fmtEuro = (n: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

const fmtData = (iso: string) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

const PAGE_SIZE = 50;

const RegistroMovimentiView: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentSedeId } = useAuth();
    const { addToast } = useToast();

    // Di default il registro è generale (ALL); il selettore permette di limitarlo
    // a mandati o servizi. Il valore iniziale può arrivare da query string.
    const [preFilter, setPreFilter] = useState<RegistroMovimentiPreFilter>(
        parsePreFilter(searchParams.get('pre'))
    );

    // Filtri
    const [preset, setPreset] = useState<PeriodoPreset>('MESE');
    const [customFrom, setCustomFrom] = useState<string>(REGISTRO_START_DATE);
    const [customTo, setCustomTo] = useState<string>(todayIso());

    // Risultati
    const [movimenti, setMovimenti] = useState<MovimentoContabile[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(0);

    // Modifica/eliminazione riga
    const [editing, setEditing] = useState<MovimentoContabile | null>(null);
    const [deleting, setDeleting] = useState<MovimentoContabile | null>(null);
    const [savingEdit, setSavingEdit] = useState<boolean>(false);

    // KPI "Da Fatturare" / "Da Riscuotere": stato attuale (residui aperti su
    // pratiche e servizi attivi), non riferito al periodo, perché ricostruire lo
    // stato a una data passata non è supportato. Il disclaimer lo dichiara.
    const [kpiDaFatturare, setKpiDaFatturare] = useState<number>(0);
    const [kpiDaRiscuotere, setKpiDaRiscuotere] = useState<number>(0);
    const [kpiLoading, setKpiLoading] = useState<boolean>(true);

    const { from, to } = useMemo(
        () => rangeForPreset(preset, customFrom, customTo),
        [preset, customFrom, customTo]
    );

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const sedeIdForService = currentSedeId ?? 'ALL';
            const res = await AmministrazioneService.getRegistroMovimenti({
                sedeId: sedeIdForService as number | 'ALL',
                from,
                to,
                preFilter,
            });
            if (res.success && Array.isArray(res.data)) {
                setMovimenti(res.data);
                setPage(0);
            } else {
                setMovimenti([]);
                addToast(res.message || 'Errore nel caricamento dei movimenti', 'error');
            }
        } catch (e) {
            console.error('Errore caricamento registro movimenti', e);
            setMovimenti([]);
            addToast('Errore nel caricamento dei movimenti', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentSedeId, from, to, preFilter, addToast]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // I KPI cumulativi ignorano il periodo ma rispettano il filtro sorgente,
    // altrimenti i totali non quadrerebbero con i movimenti elencati.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setKpiLoading(true);
            try {
                const sedeIdForService = (currentSedeId ?? 'ALL') as number | 'ALL';
                const includePratiche = preFilter !== 'SERVIZI';
                const includeServizi  = preFilter !== 'PRATICHE';
                const [pratNonFatt, pratNonSald, servNonFatt, servNonSald] = await Promise.all([
                    includePratiche ? AmministrazioneService.getPraticheNonFatturate(sedeIdForService) : Promise.resolve({ data: [] } as any),
                    includePratiche ? AmministrazioneService.getPraticheNonSaldate(sedeIdForService) : Promise.resolve({ data: [] } as any),
                    includeServizi  ? AmministrazioneService.getServiziNonFatturati(sedeIdForService) : Promise.resolve({ data: [] } as any),
                    includeServizi  ? AmministrazioneService.getServiziNonSaldati(sedeIdForService) : Promise.resolve({ data: [] } as any),
                ]);
                if (cancelled) return;
                const sumDaFatturare =
                    (pratNonFatt.data || []).reduce((acc: number, p: any) => acc + (p.importoDaFatturare || 0), 0) +
                    (servNonFatt.data || []).reduce((acc: number, s: any) => acc + (s.importoDaFatturare || 0), 0);
                const sumDaRiscuotere =
                    (pratNonSald.data || []).reduce((acc: number, p: any) => acc + (p.importoResiduo || 0), 0) +
                    (servNonSald.data || []).reduce((acc: number, s: any) => acc + (s.importoResiduo || 0), 0);
                setKpiDaFatturare(sumDaFatturare);
                setKpiDaRiscuotere(sumDaRiscuotere);
            } catch (e) {
                console.error('Errore caricamento KPI registro movimenti', e);
            } finally {
                if (!cancelled) setKpiLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [currentSedeId, preFilter]);

    // Salvataggio modifica
    const submitEdit = useCallback(async (patch: MovimentoEditRequest) => {
        if (!editing || !editing.sourceKey) return;
        setSavingEdit(true);
        try {
            const res = await AmministrazioneService.editMovimento(editing.sourceKey, patch);
            if (res.success) {
                addToast('Movimento aggiornato', 'success');
                setEditing(null);
                await loadData();
            } else {
                addToast(res.message || 'Errore in modifica movimento', 'error');
            }
        } catch (e: any) {
            // 403 → messaggio leggibile (mandato/servizio attivo).
            const msg = e?.response?.data?.message || 'Errore in modifica movimento';
            addToast(msg, 'error');
        } finally {
            setSavingEdit(false);
        }
    }, [editing, addToast, loadData]);

    // Esporta in PDF la vista corrente con gli stessi filtri. Passiamo da Axios
    // per avere l'header di autenticazione (window.open non manderebbe il JWT).
    const exportPdf = useCallback(async () => {
        try {
            const sedeIdForService = currentSedeId ?? 'ALL';
            const blob = await AmministrazioneService.exportRegistroMovimentiPdf({
                sedeId: sedeIdForService as number | 'ALL',
                from,
                to,
                preFilter,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `registro-movimenti_${from}_${to}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
        } catch (e) {
            console.error('Errore esportazione PDF', e);
            addToast('Errore esportazione PDF', 'error');
        }
    }, [currentSedeId, from, to, preFilter, addToast]);

    const confirmDelete = useCallback(async () => {
        if (!deleting || !deleting.sourceKey) return;
        setSavingEdit(true);
        try {
            const res = await AmministrazioneService.deleteMovimento(deleting.sourceKey);
            if (res.success) {
                addToast('Movimento eliminato', 'success');
                setDeleting(null);
                await loadData();
            } else {
                addToast(res.message || 'Errore in eliminazione movimento', 'error');
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Errore in eliminazione movimento';
            addToast(msg, 'error');
        } finally {
            setSavingEdit(false);
        }
    }, [deleting, addToast, loadData]);

    // Riepilogo di cassa del periodo filtrato (entrate/uscite).
    const { totEntrate, totUscite } = useMemo(() => {
        let entrate = 0;
        let uscite = 0;
        for (const m of movimenti) {
            if (m.importo >= 0) entrate += m.importo;
            else uscite += m.importo; // m.importo è già negativo
        }
        return { totEntrate: entrate, totUscite: uscite };
    }, [movimenti]);

    const totalePages = Math.max(1, Math.ceil(movimenti.length / PAGE_SIZE));
    const pageMovimenti = useMemo(
        () => movimenti.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
        [movimenti, page]
    );

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                        title="Indietro"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
                            <Receipt className="w-6 h-6 text-indigo-600" />
                            Registro Movimenti
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">{sottotitolo(preFilter)}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {/* Esporta PDF della vista corrente (con i filtri) */}
                    <button
                        onClick={exportPdf}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        title="Scarica PDF della vista corrente"
                    >
                        <FileDown className="w-4 h-4" />
                        <span className="text-sm">Esporta PDF</span>
                    </button>
                    <button
                        onClick={loadData}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        title="Aggiorna"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        <span className="text-sm">{loading ? 'Aggiornamento…' : 'Aggiorna'}</span>
                    </button>
                </div>
            </div>

            {/* Filtri: sorgente + periodo */}
            <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 space-y-2.5">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-medium w-16">
                        <Filter className="w-3.5 h-3.5" /> Tipo
                    </span>
                    {([
                        ['ALL', 'Tutti'],
                        ['PRATICHE', 'Servizi investigativi'],
                        ['SERVIZI', 'Servizi fiduciari'],
                    ] as [RegistroMovimentiPreFilter, string][]).map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setPreFilter(k)}
                            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                preFilter === k
                                    ? 'bg-slate-800 text-white border-slate-800'
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2.5">
                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-medium w-16">
                        <Calendar className="w-3.5 h-3.5" /> Periodo
                    </span>
                    {([
                        ['INIZIO', "Dall'inizio"],
                        ['MESE', '1 mese'],
                        ['MESI_3', '3 mesi'],
                        ['MESI_6', '6 mesi'],
                        ['ANNO', '1 anno'],
                        ['CUSTOM', 'Personalizzato'],
                    ] as [PeriodoPreset, string][]).map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setPreset(k)}
                            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                preset === k
                                    ? 'bg-slate-800 text-white border-slate-800'
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            {label}
                        </button>
                    ))}

                    {preset === 'CUSTOM' && (
                        <>
                            <input
                                type="date"
                                value={customFrom}
                                min={REGISTRO_START_DATE}
                                max={customTo || todayIso()}
                                onChange={(e) => setCustomFrom(e.target.value)}
                                className="ml-2 px-2 py-1 border border-slate-200 rounded-md text-xs text-slate-700"
                            />
                            <span className="text-slate-400 text-xs">→</span>
                            <input
                                type="date"
                                value={customTo}
                                min={customFrom || REGISTRO_START_DATE}
                                max={todayIso()}
                                onChange={(e) => setCustomTo(e.target.value)}
                                className="px-2 py-1 border border-slate-200 rounded-md text-xs text-slate-700"
                            />
                        </>
                    )}

                    <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">
                        {fmtData(from)} → {fmtData(to)}
                    </span>
                </div>
            </div>

            {/*
              KPI: Entrate / Uscite / Saldo del periodo, più Da Fatturare / Da Riscuotere
              come stato corrente. Skeleton durante il caricamento per evitare salti di layout.
            */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
                <KpiCard
                    label="Entrate del periodo"
                    value={totEntrate}
                    loading={loading}
                    accent="emerald"
                    valueClass="text-emerald-700"
                />
                <KpiCard
                    label="Uscite del periodo"
                    value={Math.abs(totUscite)}
                    loading={loading}
                    accent="rose"
                    valueClass="text-rose-700"
                />
                <KpiCard
                    label="Saldo del periodo"
                    value={totEntrate + totUscite}
                    loading={loading}
                    accent="slate"
                    valueClass={totEntrate + totUscite >= 0 ? 'text-slate-800' : 'text-rose-700'}
                    footnote="Flusso di cassa nel periodo filtrato"
                />
                <KpiCard
                    label="Da fatturare"
                    value={kpiDaFatturare}
                    loading={kpiLoading}
                    accent="amber"
                    valueClass="text-slate-800"
                    footnote={`Cumulativo dal ${fmtData(REGISTRO_START_DATE)} ad oggi`}
                />
                <KpiCard
                    label="Da riscuotere"
                    value={kpiDaRiscuotere}
                    loading={kpiLoading}
                    accent="indigo"
                    valueClass="text-slate-800"
                    footnote={`Cumulativo dal ${fmtData(REGISTRO_START_DATE)} ad oggi`}
                />
            </div>

            {/* Tabella */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Data</th>
                                <th className="text-left px-3 py-2 font-medium">Categoria</th>
                                <th className="text-left px-3 py-2 font-medium">Riferimento</th>
                                <th className="text-left px-3 py-2 font-medium">Sede</th>
                                <th className="text-left px-3 py-2 font-medium">Descrizione</th>
                                <th className="text-right px-3 py-2 font-medium">Importo</th>
                                <th className="px-3 py-2 font-medium text-center">Fattura</th>
                                <th className="px-3 py-2 font-medium text-center">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                                        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                                        Caricamento movimenti…
                                    </td>
                                </tr>
                            ) : pageMovimenti.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                                        Nessun movimento nel periodo selezionato.
                                    </td>
                                </tr>
                            ) : (
                                pageMovimenti.map((m) => (
                                    <tr key={m.sourceKey || m.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtData(m.data)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colorePerCategoria(m.category)}`}>
                                                {labelCategoria(m.category)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">
                                            <div className="flex items-center gap-1.5">
                                                <span>{m.praticaRef || '—'}</span>
                                                {m.orphan && (
                                                    <span className="inline-flex items-center gap-0.5 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded text-[10px]" title="Mandato/servizio eliminato">
                                                        <AlertTriangle className="w-3 h-3" /> orfano
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-slate-600">{m.sedeNome || '—'}</td>
                                        <td className="px-3 py-2 text-slate-700">{m.descrizione}</td>
                                        <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${m.importo >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                            {fmtEuro(m.importo)}
                                        </td>
                                        <td className="px-3 py-2 text-center whitespace-nowrap">
                                            {/*
                                              "Fattura emessa": il BE lo valorizza solo per le entrate di mandati o servizi,
                                              negli altri casi mostriamo "—".
                                            */}
                                            {m.fatturato === true ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-[11px] font-medium" title="Fattura elettronica emessa">
                                                    ✓ Sì
                                                </span>
                                            ) : m.fatturato === false ? (
                                                <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-[11px] font-medium" title="Da fatturare">
                                                    ✗ No
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            {/*
                                              Dal registro si modificano solo i movimenti non collegati di pratiche/servizi
                                              (per gli altri c'è il lucchetto), le righe del ledger di mandati/servizi eliminati
                                              e sempre le entrate/uscite una tantum.
                                            */}
                                            {m.eliminabile ? (
                                                <button
                                                    onClick={() => setDeleting(m)}
                                                    className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                                                    title="Elimina: collaboratore e mandato/servizio non esistono più"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            ) : m.sourceKey?.startsWith('MANUALE:') || (m.orphan && (m.sourceKey?.startsWith('PRATICA:') || m.sourceKey?.startsWith('SERVIZIO:') || m.sourceKey?.startsWith('ELIMINATO:'))) ? (
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        onClick={() => setEditing(m)}
                                                        className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                                                        title="Modifica movimento"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleting(m)}
                                                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                                                        title="Elimina movimento"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span
                                                    className="inline-flex items-center text-slate-400"
                                                    title={m.sourceKey?.startsWith('SPESA_REPORT:') || m.sourceKey?.startsWith('COMPENSO_REL:')
                                                        ? 'Si modifica dal report, nell\'agenda del collaboratore (Utenti)'
                                                        : m.sourceKey?.startsWith('SPESA_') ? 'Si gestisce dall\'approvazione spese' : 'Modifica dalla scheda mandato/servizio'}
                                                >
                                                    <Lock className="w-4 h-4" />
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginazione */}
                {!loading && movimenti.length > PAGE_SIZE && (
                    <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2 text-sm text-slate-600">
                        <span>
                            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, movimenti.length)} di {movimenti.length}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0}
                                className="px-3 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                            >
                                ← Precedente
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(totalePages - 1, p + 1))}
                                disabled={page >= totalePages - 1}
                                className="px-3 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Successiva →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modale modifica riga */}
            {editing && (
                <EditMovimentoModal
                    movimento={editing}
                    saving={savingEdit}
                    onCancel={() => setEditing(null)}
                    onSubmit={submitEdit}
                />
            )}

            {/* Conferma eliminazione */}
            {deleting && (
                <DeleteMovimentoModal
                    movimento={deleting}
                    saving={savingEdit}
                    onCancel={() => setDeleting(null)}
                    onConfirm={confirmDelete}
                />
            )}
        </div>
    );
};

// KpiCard: card per i 5 indicatori, bianca con barra laterale colorata
// e skeleton sul valore durante il caricamento.

type KpiAccent = 'emerald' | 'rose' | 'slate' | 'amber' | 'indigo';

const ACCENT_BAR: Record<KpiAccent, string> = {
    emerald: 'bg-emerald-500',
    rose:    'bg-rose-500',
    slate:   'bg-slate-400',
    amber:   'bg-amber-500',
    indigo:  'bg-indigo-500',
};

interface KpiCardProps {
    label: string;
    value: number;
    loading: boolean;
    accent: KpiAccent;
    valueClass?: string;
    footnote?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, loading, accent, valueClass, footnote }) => {
    return (
        <div className="relative bg-white border border-slate-200 rounded-lg p-4 overflow-hidden">
            <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${ACCENT_BAR[accent]}`} />
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
            <div className={`mt-1.5 text-[22px] leading-tight font-semibold tabular-nums ${valueClass || 'text-slate-800'}`}>
                {loading ? (
                    <div className="h-7 w-28 bg-slate-100 rounded animate-pulse" />
                ) : (
                    fmtEuro(value)
                )}
            </div>
            {footnote && (
                <div className="mt-2 text-[10px] text-slate-400 leading-snug">{footnote}</div>
            )}
        </div>
    );
};

// Modali modifica/eliminazione movimento

interface EditMovimentoModalProps {
    movimento: MovimentoContabile;
    saving: boolean;
    onCancel: () => void;
    onSubmit: (patch: MovimentoEditRequest) => void;
}

const EditMovimentoModal: React.FC<EditMovimentoModalProps> = ({ movimento, saving, onCancel, onSubmit }) => {
    const isAcconto = (movimento.sourceKey || '').endsWith(':acconto');
    const isServizio = (movimento.sourceKey || '').startsWith('SERVIZIO:');
    // Righe del ledger di mandati/servizi eliminati: il tipo non si cambia,
    // si modificano solo data, descrizione e importo.
    const isEliminato = (movimento.sourceKey || '').startsWith('ELIMINATO:');
    // Entrate/uscite una tantum: tipo modificabile e "fatturata" sulle entrate.
    const isManuale = (movimento.sourceKey || '').startsWith('MANUALE:');
    // Il flag "fatturato" ha senso solo sugli incassi (entrate).
    const isEntrata = (movimento.tipo || '') === 'entrata';

    const [data, setData] = useState<string>(movimento.data || todayIso());
    const [descrizione, setDescrizione] = useState<string>(movimento.descrizione || '');
    const [importoAbs, setImportoAbs] = useState<number>(Math.abs(movimento.importo || 0));
    const [tipo, setTipo] = useState<string>(
        movimento.tipo || (isServizio ? 'INCASSO' : 'entrata')
    );
    const [fatturato, setFatturato] = useState<boolean>(movimento.fatturato === true);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Acconto: si modifica solo l'importo.
        if (isAcconto) {
            onSubmit({ importo: importoAbs });
            return;
        }
        const patch: MovimentoEditRequest = {
            data,
            descrizione: descrizione.trim(),
            importo: importoAbs,
        };
        // Il tipo si invia solo per le righe vive (PRATICA/SERVIZIO); per gli
        // eliminati il BE mantiene quello esistente.
        if (!isEliminato) patch.tipo = tipo;
        // Flag fatturato: solo sugli incassi eliminati.
        if (isEliminato && isEntrata) patch.fatturato = fatturato;
        if (isManuale) patch.fatturato = tipo === 'entrata' && fatturato;
        onSubmit(patch);
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">Modifica movimento</h3>
                <p className="text-xs text-slate-500 mb-4">{movimento.praticaRef}</p>

                {isAcconto ? (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                        L'acconto del mandato è un campo scalare: si può modificare solo l'importo.
                    </div>
                ) : (
                    <>
                        <label className="block text-sm text-slate-700 mb-3">
                            <span className="block mb-1">Data</span>
                            <input
                                type="date"
                                value={data}
                                onChange={(e) => setData(e.target.value)}
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                required
                            />
                        </label>
                        <label className="block text-sm text-slate-700 mb-3">
                            <span className="block mb-1">Descrizione</span>
                            <input
                                type="text"
                                value={descrizione}
                                onChange={(e) => setDescrizione(e.target.value)}
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                placeholder="Es. Saldo finale Mario Rossi"
                            />
                        </label>
                        {!isEliminato && (
                            <label className="block text-sm text-slate-700 mb-3">
                                <span className="block mb-1">Tipo</span>
                                <select
                                    value={tipo}
                                    onChange={(e) => setTipo(e.target.value)}
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                >
                                    {isServizio ? (
                                        <>
                                            <option value="INCASSO">Incasso</option>
                                            <option value="ANTICIPO">Anticipo</option>
                                            <option value="RIMBORSO">Rimborso</option>
                                            <option value="ALTRO">Altro</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="entrata">Entrata</option>
                                            <option value="uscita">Uscita</option>
                                        </>
                                    )}
                                </select>
                            </label>
                        )}
                    </>
                )}

                <label className="block text-sm text-slate-700 mb-4">
                    <span className="block mb-1">Importo (€)</span>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={importoAbs}
                        onChange={(e) => setImportoAbs(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                        required
                    />
                </label>

                {/* "Fattura emessa": solo per gli incassi di mandati eliminati. */}
                {((isEliminato && isEntrata) || (isManuale && tipo === 'entrata')) && (
                    <label className="flex items-center gap-2 text-sm text-slate-700 mb-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fatturato}
                            onChange={(e) => setFatturato(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span>Fattura elettronica emessa</span>
                    </label>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={saving}
                        className="px-3 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                        {saving ? 'Salvataggio…' : 'Salva'}
                    </button>
                </div>
            </form>
        </div>
    );
};

interface DeleteMovimentoModalProps {
    movimento: MovimentoContabile;
    saving: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const DeleteMovimentoModal: React.FC<DeleteMovimentoModalProps> = ({ movimento, saving, onCancel, onConfirm }) => {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl">
                <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-rose-600" />
                    Eliminare il movimento?
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                    Verrà rimosso dal registro <strong>{movimento.praticaRef}</strong>: <em>{movimento.descrizione}</em> · {fmtEuro(movimento.importo)}.
                </p>
                <p className="text-xs text-slate-500 mb-4">
                    {movimento.eliminabile
                        ? (movimento.sourceKey?.startsWith('COMPENSO_REL:')
                            ? 'Ore e km approvati del report vanno a zero: il compenso sparisce e il report resta nello storico. Non si può annullare.'
                            : 'La spesa viene tolta dal report, le altre spese della giornata restano. Non si può annullare.')
                        : movimento.sourceKey?.startsWith('MANUALE:')
                            ? 'La voce una tantum viene eliminata definitivamente. Non si può annullare.'
                            : 'La riga viene azzerata nel JSON sorgente (importo = 0) per preservare gli ID degli altri movimenti. È un\'operazione reversibile editandola.'}
                </p>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        disabled={saving}
                        className="px-3 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                        Annulla
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={saving}
                        className="px-3 py-1.5 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                    >
                        {saving ? 'Eliminazione…' : 'Elimina'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RegistroMovimentiView;
